import React, { useState } from 'react';
import { Layout, Seo } from '@layout';
import { graphql } from 'gatsby';
import { ConnectedGuestListSection, SmartRSVPSectionV2 } from '@sections/wedding-invitations';
import { HighQualityPrintsSection as HighQualityPrintsSectionV2 } from '@sections/shared/HighQualityPrintsSection';
import { AbcSection } from '@sections/shared/AbcSection';
import { GalleryPageContent } from '@components/GalleryComponents/GalleryPageContent/GalleryPageContent';
import { invitationDesignsCanonicalUrl } from '@utils/gallery.util';
import { useMemo } from 'react';
import { createFilterGroupsProcessor, useProcessPrintCards } from '@utils/joyPrint.utils';
import { HowItWorksSection } from '@sections/shared/HowItWorksSection';
import { FAQSection } from '@sections/shared/FAQSection';
import { HeroSectionWithProps } from '@sections/shared/HeroSectionWithProps';
import { ValuePropsArg } from '@components/ValuePropsSection';
import { useTranslation } from 'react-i18next';
import { SEOContentSection } from '@sections/shared/SEOContentSection';

export const query = graphql`
  {
    bliss {
      stationeryTemplatesContainer(filter: { categories: invitation }, includeOptions: { includeProducts: true }) {
        pagedResults {
          edges {
            node {
              themeId
              thumbnailUrl
              approved
              enabled
              category
              productIds
            }
          }
        }
      }
      stationeryProducts {
        id
        priceConfigId
        vendor
        productType
        vendorBaseSKU
        pricing {
          __typename

          ... on Bliss_StationeryTieredPricing {
            priceTiers {
              minQuantity
              unitPriceInMinorUnits
            }
          }
          ... on Bliss_StationeryTieredFlatFeePricing {
            priceTiers {
              minQuantity
              totalPriceInMinorUnits
            }
          }
          ... on Bliss_StationeryFlatFeePricing {
            priceInMinorUnits
          }
        }
      }
      themeVariants(variantTypes: [invite]) {
        variationType
        imageUrl
        printUrl
        vendor
        tags
        colors
        font
        variantColor
        variantName
        variantThemeId
        theme {
          id
          name
          themeId
          marketingData {
            variationType
            imageUrl
            printUrl
            vendor
            tags
            colors
            font
            variantColor
            variantName
            variantThemeId
            theme {
              name
              themeId
            }
          }
        }
      }
    }
  }
`;

const CATEGORIES_IMAGES_DATA = {
  bold: 'invites-gallery/styles/bold.png',
  classic: 'invites-gallery/styles/classic.png',
  cultural: 'invites-gallery/styles/cultural.png',
  destination: 'invites-gallery/styles/destination.png',
  'floral-and-botanical': 'invites-gallery/styles/floral_botanical.png',
  metallic: 'invites-gallery/styles/metallic.png',
  modern: 'invites-gallery/styles/modern.png',
  photo: 'invites-gallery/styles/photo.png',
  rustic: 'invites-gallery/styles/rustic.png',
  seasonal: 'invites-gallery/styles/seasonal.png',
  textured: 'invites-gallery/styles/textured.png',
};

const page: MarcomPageName = 'wedding-invitations';

const WeddingInvitationsDesigns = ({ data }: { data: BlissGalleryECardsThemesData }) => {
  const { t, i18n } = useTranslation('weddingInvitations');
  const faq = t('faq');
  const highQualityPrintsSectionV2 = t('highQualityPrintsSectionV2');
  const howItWorksSection = t('howItWorksSection');
  const hero = t('hero');
  const seo = t('seo');
  const seoContent = t('seoContent');
  const productTypes = t('productTypes', { ns: 'galleryPage' });

  const { cards, setShowDesignsWithMatchingPrints } = useProcessPrintCards(data, 'invite');
  const [chromeless, setChromeless] = useState(false);

  const processFilterGroups = useMemo(() => {
    return createFilterGroupsProcessor(productTypes.saveTheDate as GalleryProductType, data.bliss);
  }, [data, productTypes]);

  const valueProps: ValuePropsArg[] = useMemo(
    () =>
      t('valueProps').map((valueProp) => ({
        ...valueProp,
        icon: valueProp.icon as ValuePropsArg['icon'],
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );

  const valuePropsMobile: ValuePropsArg[] = useMemo(
    () =>
      t('valuePropsMobile').map((valueProp) => ({
        ...valueProp,
        icon: valueProp.icon as ValuePropsArg['icon'],
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );

  return (
    <Layout
      isTransparentHeader={true}
      transparentJoyLogoColor="mono7"
      transparentHeaderButtonVariant="defaultPrimeWeddingWebsites"
      hideValueProps
      page={page}
      chromeless={chromeless}
    >
      <Seo
        title={seo.title}
        description={seo.content}
        canonicalURL={invitationDesignsCanonicalUrl}
        ogImage={'common/meta_share/invites.jpg'}
      />
      <HeroSectionWithProps
        page={page}
        heroData={hero}
        valueProps={valueProps}
        valuePropsMobile={valuePropsMobile}
        valuePropsElementId="wedding-invitations-props"
        sectionId="Wedding_Invitations_Hero"
        ctaButtonId="Wedding_Invitations_Hero_CTA"
      />
      <GalleryPageContent
        page={page}
        productType="Invitation"
        cards={cards}
        processFilterGroups={processFilterGroups}
        onShowDesignsWithMatchingPrintsToggle={setShowDesignsWithMatchingPrints}
        showModal={(isModalOpened) => {
          setChromeless(isModalOpened);
        }}
        displayHeroSection={false}
        initialProductCount={24}
        displayFilterTab={false}
        defaultProductType={productTypes.prints as GalleryProductType}
        sectionId="gallery-grid-wrapper"
        displayCategoryImages
        categoryImagesData={CATEGORIES_IMAGES_DATA}
        hideCategoryImagesScrollbar
      />
      <HighQualityPrintsSectionV2 highQualityPrintsSectionData={highQualityPrintsSectionV2} />
      <HowItWorksSection howItWorksSectionData={howItWorksSection} />
      <SmartRSVPSectionV2 />
      <ConnectedGuestListSection />
      <FAQSection
        id="wedding-invites-faq"
        title={faq.title}
        description={faq.description}
        questionNodes={t('questions.weddingInvitationDesignV2', { ns: 'faq' })}
      />
      <SEOContentSection
        id="wedding-invitations-seo-content"
        nodes={seoContent.nodes}
        ctaLabel={seoContent.ctaLabel}
        reviews={seoContent.reviews}
      />
      <AbcSection page={page} abcSectionId="Wedding_Invitation_Design_ABC_CTA" />
    </Layout>
  );
};

export default WeddingInvitationsDesigns;
